import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle,
  CircleCheckBig,
  CircleDot,
  CircleMinus,
  Coins,
  CreditCard,
  DollarSign,
  FileText,
  Grid2X2,
  Loader2,
  ReceiptText,
  Type,
} from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import { Suspense } from "react";
import {
  capitalize,
  categoryDisplayNames,
  formatCurrency,
  formatDate,
  isTransactionValid,
} from "shared";
import { toast } from "sonner";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { categoryIconMap } from "@/components/fields/spend-category-multi-select-field";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderAllowedCategories } from "@/components/table/cells/RenderAllowedCategories";
import {
  DashedOutCell,
  RenderCell,
} from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import { getTimestampForFilename } from "@/components/table/tableUtils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import type { RouterOutputs } from "@/lib/trpcClient";
import {
  BulkTransactionAssignField,
  SingleTransactionAssignField,
} from "@/routes/_authed/_org/transactions/-components/transaction-budget-assign";
import { ApprovalStatusBadge } from "./-components/approval-status-badge";

export const Route = createFileRoute("/_authed/_org/transactions/card")({
  component: RouteComponent,
});

type TransactionType = RouterOutputs["transaction"]["list"][number];
const dtf = createColumnConfigHelper<TransactionType>();
const columnsConfig = [
  dtf
    .text()
    .id("Merchant")
    .accessor((row) => row.merchant?.description ?? row.merchant?.name)
    .displayName("Merchant")
    .icon(Type)
    .build(),
  dtf
    .number()
    .id("Amount")
    .accessor((row) => row.amountCents / 100)
    .displayName("Amount")
    .icon(Coins)
    .build(),
  dtf
    .date()
    .id("Date")
    .displayName("Date")
    .accessor((row) => row.originatedAt)
    .icon(Calendar)
    .build(),

  dtf
    .option()
    .id("Budget")
    .accessor(
      (row) =>
        row.budget ?? {
          id: "null",
          name: "Not Assigned",
        },
    )
    .displayName("Budget")
    .icon(DollarSign)
    .transformOptionFn((u) => {
      if (u.id === "null") {
        return {
          icon: <AlertCircle className="text-red-600" size={14} />,
          label: "Not Assigned",
          value: "null",
        };
      }
      return {
        label: u.name,
        value: u.id,
      };
    })
    .build(),
  dtf
    .option()
    .id("Category")
    .accessor((row) => row.category)
    .displayName("Spend Category")
    .icon(Grid2X2)
    .transformOptionFn((u) => {
      return {
        icon: (
          <DynamicIcon
            name={categoryIconMap[u]}
            className="size-3.5 text-neutral-500"
          />
        ),
        label: categoryDisplayNames[u],
        value: u,
      };
    })
    .build(),
  dtf
    .option()
    .id("Card")
    .accessor((row) => row.card)
    .displayName("Card")
    .icon(CreditCard)
    .transformOptionFn((u) => {
      return {
        label: u.name,
        value: u.id,
      };
    })
    .build(),
  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .displayName("Status")
    .icon(CircleDot)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .hide()
    .build(),
];

const initialFilterState: FiltersState = [
  {
    columnId: "Status",
    hide: true,
    operator: "is not",
    type: "option",
    values: ["declined"],
  },
];

function RouteComponent() {
  const location = useLocation();
  const navigate = Route.useNavigate();
  const { trpc } = Route.useRouteContext();
  const { data: orgUsers, isLoading: isUserMapLoading } = useQuery(
    trpc.org.users.queryOptions(),
  );

  const { data: transactions, isLoading: isTransactionsLoading } = useQuery(
    trpc.transaction.list.queryOptions(),
  );

  const isLoading = isUserMapLoading || isTransactionsLoading;

  const { bookkeeper } = useRequiredRole();

  const tableColumns: ColumnDef<TransactionType>[] = [
    {
      accessorFn: (row) => row.merchant?.description ?? row.merchant?.name,
      cell: ({ row: { original } }) => {
        return (
          <RenderCell
            merchant={original.merchant}
            merchantCategory={original.category}
            type="merchant"
          />
        );
      },
      id: "Merchant",
    },
    {
      accessorFn: (row) => row.originatedAt,
      cell: ({ row }) => (
        <RenderCell date={row.original.originatedAt} type="date" />
      ),
      exportAccessorFn: (row) => formatDate(row.originatedAt),
      id: "Date",
    },
    {
      accessorFn: (row) => row.userId,
      cell: ({ row }) => {
        return (
          orgUsers && (
            <RenderCell
              type="cardHolder"
              user={orgUsers.userMap[row.original.userId]}
            />
          )
        );
      },
      enableColumnFilter: true,
      exportAccessorFn: (row) => orgUsers?.userMap[row.userId].name,
      id: "Cardholder",
    },

    {
      accessorFn: (row) => row.category,
      cell: ({ row: { original } }) => (
        <RenderAllowedCategories allowedCategories={[original.category]} />
      ),
      id: "Category",
    },
    {
      //* Converting null to "null" helps with sorting
      accessorFn: (row) => row.budgetId ?? "null",
      cell: ({ row: { original } }) => {
        if (!isTransactionValid(original.status)) {
          return (
            <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 font-medium text-xs">
              <AlertCircle className="text-gray-600" size={14} />
              <span>N/A</span>
            </div>
          );
        }
        return (
          <SingleTransactionAssignField
            transaction={original}
            className="w-full"
          />
        );
      },
      exportAccessorFn: (row) => row.budget?.name ?? "NOT ASSIGNED",
      id: "Budget",
    },
    {
      accessorFn: (row) => row.policySatisfied,
      cell: ({ row }) => {
        if (!isTransactionValid(row.original.status)) {
          return <CircleMinus className="mr-1.5 size-4 text-gray-800" />;
        }

        if (row.original.policySatisfied) {
          return <CircleCheckBig className="mr-1.5 size-4 text-green-800" />;
        }

        return <AlertTriangle className="mr-1.5 size-4 text-amber-800" />;
      },
      id: "Policy",
    },
    {
      accessorFn: (row) => row.approvalStatus?.status,
      cell: ({ row: { original: row } }) =>
        row.approvalStatus ? (
          <ApprovalStatusBadge {...row.approvalStatus} withAction />
        ) : (
          <DashedOutCell />
        ),
      id: "Approval",
    },
    {
      accessorFn: (row) => row.card?.id,
      cell: ({ row: { original: row } }) =>
        row.card ? (
          <RenderCell card={row.card} type="card" />
        ) : (
          <DashedOutCell />
        ),
      exportAccessorFn: (row) => row.card?.name,
      id: "Card",
    },
    {
      accessorFn: (row) => row.status,
      accessorKey: "Status",
      cell: ({ row }) => (
        <RenderCell status={row.original.status} type="status" />
      ),
      id: "Status",
    },
    {
      accessorFn: (row) => row.amountCents,
      cell: ({ row }) => (
        <RenderCell amount={row.original.amountCents} type="currency" />
      ),
      exportAccessorFn: (row) => formatCurrency(row.amountCents),
      id: "Amount",
      meta: {
        align: "right",
      },
    },
  ];

  const columnPinning = {
    left: ["Merchant"],
    right: ["Amount"],
  };

  const { table, filters, actions, columns, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data: transactions ?? [],
      filterOptions: {
        columnsConfig,
        defaultFilters: initialFilterState,
        strategy: "client",
      },
      getRowId: (row) => row.id.toString(),
      initialState: {
        columnVisibility: {
          Category: false,
        },
      },
      state: {
        columnPinning,
      },
      tableName: "transactions",
      version: "v4",
    });

  const view = getViewFromFilters(filters);

  const updateStatusFilter = (status: "declined" | "approved") => {
    setTableFilters((filters) =>
      filters.map((f) =>
        f.columnId === "Status"
          ? {
              ...f,
              operator: status === "approved" ? "is not" : "is",
            }
          : f,
      ),
    );
  };

  const { mutate: exportReceipts, isPending: isExportingReceipts } =
    useMutation(
      trpc.transaction.exportReceipts.mutationOptions({
        onError: (error) => {
          toast.error(error.message);
        },
        onSuccess: () => {
          toast.success("We're exporting your receipts", {
            description:
              "Check your email in a few minutes. We'll send you a link when your export is ready for download.",
          });
        },
      }),
    );

  const tableName = "transactions";
  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/transactions",
          });
        }
      }}
      open={
        location.pathname !== "/transactions/card" &&
        location.pathname.includes("/transactions/card")
      }
    >
      <Page
        heading="Transactions"
        table={{
          actions,
          columnPinning,
          columns,
          exportFileName: `Givefront_Transactions_${getTimestampForFilename()}`,
          filters,
          strategy,
          table,
        }}
        tabs={
          <Tabs
            onValueChange={(v) => {
              //@ts-ignore
              updateStatusFilter(v);
            }}
            value={view}
          >
            <TabsList variant="standard">
              <TabsTrigger value="approved" variant="standard">
                <span className="flex flex-row items-center gap-2">
                  <CheckCircle className="size-3.5 text-neutral-400" />
                  Valid
                </span>
              </TabsTrigger>
              <TabsTrigger value="declined" variant="standard">
                <span className="flex flex-row items-center gap-2">
                  <AlertTriangle className="size-3.5 text-neutral-400" />
                  Declined
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
        actions={
          bookkeeper && (
            <Link
              to="/organization/statements"
              search={{
                tab: "statements",
              }}
            >
              <Button className="ml-2" variant={"outline"}>
                <FileText />
                View Statements
              </Button>
            </Link>
          )
        }
        secondaryActions={
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              exportReceipts({
                transactionIds: table
                  .getFilteredRowModel()
                  .rows.map((r) => r.original.id),
              })
            }
            disabled={isExportingReceipts}
          >
            {isExportingReceipts ? (
              <Loader2 className="animate-spin" />
            ) : (
              <ReceiptText />
            )}
            Export Receipts
          </Button>
        }
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Transactions</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName="transactions"
          navOptions={(row) =>
            linkOptions({
              params: {
                id: row.original.id,
              },
              to: "/transactions/card/$id",
            })
          }
          loading={isLoading}
          className="[&_th]:border-t-0!"
          selectable={view === "approved"}
        />

        <TablePagination
          tableName={tableName}
          table={table}
          leftSlot={
            table.getFilteredSelectedRowModel().rows.length > 0 && (
              <>
                <span className="-mx-2 text-nowrap font-normal text-neutral-600 text-sm">
                  {formatCurrency(
                    table
                      .getFilteredSelectedRowModel()
                      .rows.reduce((acc, t) => acc + t.original.amountCents, 0),
                  )}{" "}
                  USD
                </span>

                <div className="mx-4 h-8 w-px bg-neutral-300 last:hidden" />

                <BulkTransactionAssignField
                  transactions={table
                    .getFilteredSelectedRowModel()
                    .rows.map((r) => r.original)}
                />
              </>
            )
          }
        />
      </Page>

      <SheetContent
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        size={"large"}
      >
        <Suspense
          fallback={
            <div className="px-4">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}

function getViewFromFilters(filters: FiltersState) {
  const transactionStatus = filters.find(
    (filter) => filter.columnId === "Status",
  );
  if (transactionStatus?.operator === "is") {
    return "declined" as const;
  }

  return "approved" as const;
}
